// Ported verbatim from the LeadMailer Design System (components/overlays/Dialog.jsx).
import React from 'react'

export interface DialogProps {
  open?: boolean
  onClose?: () => void
  title?: React.ReactNode
  footer?: React.ReactNode
  width?: number
  children?: React.ReactNode
}

export function Dialog({ open, onClose, title, footer, width = 440, children }: DialogProps) {
  // Escape closes — the design system relies on the backdrop click, this adds
  // the keyboard equivalent without changing any visual behaviour.
  React.useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: 'var(--surface-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="dialog-panel lm-glass"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: width,
          maxHeight: '85vh',
          overflow: 'auto',
          background: 'var(--surface-raised)',
          border: '1px solid var(--glass-edge)',
          borderRadius: 'var(--glass-radius)',
          boxShadow: 'var(--shadow-overlay)',
        }}
      >
        {title && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px 0',
            }}
          >
            <div
              style={{
                fontSize: 'var(--text-md)',
                fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
                color: 'var(--text-primary)',
              }}
            >
              {title}
            </div>
            <button
              aria-label="Close"
              onClick={onClose}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: 'var(--text-muted)',
                width: 28,
                height: 28,
                borderRadius: 'var(--radius-sm)',
                fontSize: 16,
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        )}
        <div style={{ padding: 16, color: 'var(--text-secondary)', fontSize: 'var(--text-base)' }}>
          {children}
        </div>
        {footer && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              padding: '0 16px 16px',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
